import {
    createContext,
    useContext,
    type ComponentProps,
} from 'react'

import {
    Tab as AriaTab,
    TabList as AriaTabList,
    TabPanel as AriaTabPanel,
    TabPanels as AriaTabPanels,
    Tabs as AriaTabs,
} from 'react-aria-components/Tabs'

import { cn } from '@/shared/lib/cn'

type AriaTabsProps =
    ComponentProps<typeof AriaTabs>

type AriaTabListProps =
    ComponentProps<typeof AriaTabList>

type AriaTabProps =
    ComponentProps<typeof AriaTab>

type AriaTabPanelsProps =
    ComponentProps<typeof AriaTabPanels>

type AriaTabPanelProps =
    ComponentProps<typeof AriaTabPanel>

export type TabsVariant =
    | 'underline'
    | 'pills'

type TabsOrientation =
    NonNullable<
        AriaTabsProps['orientation']
    >

interface TabsStyleContextValue {
    variant: TabsVariant
    orientation: TabsOrientation
}

const TabsStyleContext =
    createContext<TabsStyleContextValue>({
        variant: 'underline',
        orientation: 'horizontal',
    })

export interface TabsProps
    extends Omit<
        AriaTabsProps,
        'className'
    > {
    variant?: TabsVariant
    className?: string
}

export function Tabs({
    variant = 'underline',
    orientation = 'horizontal',
    className,
    children,
    ...props
}: TabsProps) {
    return (
        <TabsStyleContext.Provider
            value={{
                variant,
                orientation,
            }}
        >
            <AriaTabs
                {...props}
                orientation={orientation}
                className={cn(
                    'w-full',

                    orientation === 'vertical' && [
                        'flex items-start',
                        'gap-6',
                    ],

                    className,
                )}
            >
                {children}
            </AriaTabs>
        </TabsStyleContext.Provider>
    )
}

export interface TabListProps
    extends Omit<
        AriaTabListProps,
        'className'
    > {
    className?: string
}

export function TabList({
    className,
    ...props
}: TabListProps) {
    const {
        variant,
        orientation,
    } = useContext(
        TabsStyleContext,
    )

    const isVertical =
        orientation === 'vertical'

    return (
        <AriaTabList
            {...props}
            className={cn(
                [
                    'flex',
                    'text-sm font-medium',
                ],

                isVertical
                    ? [
                        'w-56 shrink-0',
                        'flex-col',
                    ]
                    : [
                        'max-w-full',
                        'overflow-x-auto',
                        'overflow-y-hidden',
                    ],

                variant === 'underline'
                    ? isVertical
                        ? [
                            'border-r',
                            'border-(--color-border)',
                        ]
                        : [
                            'border-b',
                            'border-(--color-border)',
                        ]
                    : [
                        'gap-1',
                        'rounded-lg',
                        'bg-(--color-surface-muted)',
                        'p-1',
                    ],

                className,
            )}
        />
    )
}

export interface TabProps
    extends Omit<
        AriaTabProps,
        'className'
    > {
    className?: string
}

export function Tab({
    className,
    ...props
}: TabProps) {
    const {
        variant,
        orientation,
    } = useContext(
        TabsStyleContext,
    )

    const isVertical =
        orientation === 'vertical'

    return (
        <AriaTab
            {...props}
            className={cn(
                [
                    'flex cursor-pointer',
                    'items-center gap-2',
                    'whitespace-nowrap',
                    'outline-none',
                    'transition-colors',

                    'data-focus-visible:ring-2',
                    'data-focus-visible:ring-(--color-primary)',
                    'data-focus-visible:ring-offset-2',

                    'data-disabled:cursor-not-allowed',
                    'data-disabled:opacity-50',
                ],

                variant === 'underline'
                    ? isVertical
                        ? [
                            '-mr-px',
                            'justify-start',
                            'border-r-2',
                            'border-transparent',
                            'px-3 py-2.5',
                            'text-(--color-muted-foreground)',

                            'data-hovered:text-(--color-foreground)',
                            'data-selected:border-(--color-primary)',
                            'data-selected:text-(--color-primary)',
                        ]
                        : [
                            '-mb-px',
                            'justify-center',
                            'border-b-2',
                            'border-transparent',
                            'px-3 py-2.5',
                            'text-(--color-muted-foreground)',

                            'data-hovered:text-(--color-foreground)',
                            'data-selected:border-(--color-primary)',
                            'data-selected:text-(--color-primary)',
                        ]
                    : [
                        'justify-center',
                        'rounded-md',
                        'px-3 py-2',
                        'text-(--color-muted-foreground)',

                        'data-hovered:text-(--color-foreground)',
                        'data-selected:bg-(--color-surface)',
                        'data-selected:text-(--color-foreground)',
                        'data-selected:shadow-sm',
                    ],

                className,
            )}
        />
    )
}

export interface TabPanelsProps
    extends Omit<
        AriaTabPanelsProps,
        'className'
    > {
    className?: string
}

export function TabPanels({
    className,
    ...props
}: TabPanelsProps) {
    const {
        orientation,
    } = useContext(
        TabsStyleContext,
    )

    return (
        <AriaTabPanels
            {...props}
            className={cn(
                orientation === 'vertical' &&
                'min-w-0 flex-1',
                className,
            )}
        />
    )
}

export interface TabPanelProps
    extends Omit<
        AriaTabPanelProps,
        'className'
    > {
    className?: string
}

export function TabPanel({
    className,
    ...props
}: TabPanelProps) {
    return (
        <AriaTabPanel
            {...props}
            className={cn(
                [
                    'py-5 outline-none',

                    'data-focus-visible:rounded-md',
                    'data-focus-visible:ring-2',
                    'data-focus-visible:ring-(--color-primary)',
                    'data-focus-visible:ring-offset-2',
                ],
                className,
            )}
        />
    )
}
